// @flow
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import type { Location, Match } from 'react-router-dom';

import Loading from 'components/fragments/Loading';
import FooterSocial from 'components/footer/FooterSocial';
import JobSidebar from 'components/job/jobSidebar/JobSidebar';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import Sidebar from 'components/sidebar/Sidebar';
import SubRouter from 'components/subRouter/SubRouter';
import PageMedia from 'components/page/template/PageMedia';
import PageMediaDetailContainer from 'containers/page/PageMediaDetailContainer';
import PageNodeHeader from 'containers/page/PageNodeHeaderContainer';
import PagePartenaireHeader from 'containers/page/PagePartenaireHeaderContainer';
import { STATUS_DEFAULT, STATUS_LOADING, STATUS_UNKNOWN } from 'constants/statusConstants';
import type { AlbumType } from 'types/Albums';
import type { BlocContent } from 'types/NewsletterBloc';
import type { CardType } from 'types/PageList';
import { nullPageNode } from 'types/PageNode';
import type { PartenaireType } from 'types/Partenaires';
import type { Status } from 'types/Status';
import { handleTagEvent } from 'utils/tagManagerUtils';
import { tagsUtils } from 'utils/tagsUtils';

export type DispatchProps = {
  fetchAlbums: () => void,
  fetchPageNode: (slug: string) => void,
  fetchPartenaire: (slug: string) => void,
  resetPageNode: () => void
};

export type StateProps = {
  albums: AlbumType[],
  albums_filtres: {
    term_id: number,
    name: string,
    slug: string
  }[],
  albums_saisons: {
    term_id: number,
    name: string,
    slug: string
  }[],
  albumsStatus: Status,
  contentNode: BlocContent[],
  featured_media: {
    src: string,
    legend: string
  },
  liste_enfants: CardType[],
  sidebar: BlocContent[],
  pageDetailSidebar: BlocContent[],
  partenaire: PartenaireType,
  status: Status,
  template: string,
  pageDetailTemplate: string,
  title: string,
  titleParent: string,
  slug_parent: string
};

type Props = {
  location: Location,
  match: Match,
  pageSlug: string
} & DispatchProps &
  StateProps;

class PageNode extends Component<Props> {
  _articleContent: ?HTMLElement;
  _tags: any;

  componentDidMount() {
    const {
      fetchPageNode,
      fetchPartenaire,
      match: {
        params: { slugNode }
      },
      pageSlug
    } = this.props;

    if (slugNode) {
      if (slugNode.match('--partenaire')) {
        fetchPartenaire(slugNode.replace('--partenaire', ''));
      } else {
        fetchPageNode(slugNode);
      }
    } else {
      fetchPageNode(pageSlug);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { template, fetchAlbums, albumsStatus } = this.props;
    const { template: prevTemplate } = prevProps;

    if (this._articleContent) {
      this._tags = tagsUtils(this._articleContent);
    }

    if (
      (prevTemplate !== template && template === 'mediatheque') ||
      (albumsStatus === STATUS_DEFAULT && template === 'mediatheque')
    ) {
      fetchAlbums();
    }
  }

  componentWillUnmount() {
    if (this._tags) {
      this._tags.removeTagsEvents();
    }

    this.props.resetPageNode();
  }

  shouldComponentUpdate(nextProps: Props) {
    const { slugNode } = this.props.match.params;
    const { liste_enfants } = this.props;

    if (nextProps.status === STATUS_LOADING) {
      return false;
    }

    if (nextProps !== this.props) {
      if (nextProps.match.params.slugNode) {
        if (nextProps.match.params.slugNode !== slugNode) {
          this.props.fetchPageNode(nextProps.match.params.slugNode);
          return liste_enfants !== nextProps.liste_enfants;
        }
      }
      return true;
    }

    return false;
  }

  renderSubRouter = () => {
    const { liste_enfants, contentNode, match } = this.props;

    if (liste_enfants !== nullPageNode.liste_enfants) {
      return <SubRouter subroutes={liste_enfants} match={match} />;
    }

    return (
      <div className="page">
        {contentNode.map((blocFigure, index) => {
          return <ContentBloc key={index} {...blocFigure} />;
        })}
      </div>
    );
  };

  renderPageNodeHeader = () => {
    const { liste_enfants } = this.props;

    if (liste_enfants !== nullPageNode.liste_enfants) {
      return <PageNodeHeader withSubMenu={true} />;
    } else {
      return <PageNodeHeader withSubMenu={false} />;
    }
  };

  clickTagManager = (socialName: string) => {
    const {
      partenaire: { title }
    } = this.props;
    return handleTagEvent('partage', `partage_${title}`, socialName);
  };

  _renderContentPartenaire = () => {
    const { partenaire } = this.props;
    let sidebarContent;
    if (partenaire.sidebar && partenaire.sidebar.length > 0) {
      sidebarContent = partenaire.sidebar;
    }

    return (
      <>
        <div className="col col-md-8 col-lg-9 bg-overflow">
          <section>
            <PagePartenaireHeader />
            <div className="row">
              <div className="col col-lg-8on9">
                <article className="article clearfix">
                  <section>
                    <div className="row">
                      <div className="col col-lg-9on9">
                        <div className="bb1">
                          {partenaire.featured_media.src && (
                            <div className="block-partner block-partner--detail mt-2 mb-4">
                              <div className="block-partner__left">
                                <img src={partenaire.featured_media.src} alt="" />
                              </div>
                            </div>
                          )}
                          {(partenaire.facebook ||
                            partenaire.twitter ||
                            partenaire.instagram ||
                            partenaire.youtube ||
                            partenaire.linkedin ||
                            partenaire.lien) && (
                            <div
                              className="liens-structure mb-3"
                              style={{ display: 'flex', justifyContent: 'space-between' }}
                            >
                              <div className="social">
                                {partenaire.facebook && (
                                  <a
                                    href={partenaire.facebook}
                                    title="Facebook"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="label label--blue label--icon">
                                      <i className="icon icon-facebook"></i>
                                    </span>
                                    &nbsp;&nbsp;
                                  </a>
                                )}
                                {partenaire.twitter && (
                                  <a
                                    href={partenaire.twitter}
                                    title="Twitter"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="label label--blue label--icon">
                                      <i className="icon icon-twitter"></i>
                                    </span>
                                    &nbsp;&nbsp;
                                  </a>
                                )}
                                {partenaire.instagram && (
                                  <a
                                    href={partenaire.instagram}
                                    title="Instagram"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="label label--blue label--icon">
                                      <i className="icon icon-instagram"></i>
                                    </span>
                                    &nbsp;&nbsp;
                                  </a>
                                )}
                                {partenaire.youtube && (
                                  <a
                                    href={partenaire.youtube}
                                    title="Youtube"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="label label--blue label--icon">
                                      <i className="icon icon-youtube"></i>
                                    </span>
                                    &nbsp;&nbsp;
                                  </a>
                                )}
                                {partenaire.linkedin && (
                                  <a
                                    href={partenaire.linkedin}
                                    title="Linkedin"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="label label--blue label--icon">
                                      <i className="icon icon-linkedin"></i>
                                    </span>
                                    &nbsp;&nbsp;
                                  </a>
                                )}
                              </div>
                              <div className="lien ft-700">
                                {partenaire.lien && (
                                  <>
                                    <a href={partenaire.lien} target="_blank" rel="noopener noreferrer">
                                      {partenaire.lien_cta ? partenaire.lien_cta : 'Accéder au site'}
                                    </a>
                                    <span>
                                      &nbsp;&nbsp;<i className="icon icon-external"></i>
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="page">
                          {partenaire.content.data.map((blocFigure, index) => {
                            return <ContentBloc key={index} {...blocFigure} />;
                          })}
                        </div>
                      </div>
                    </div>
                  </section>
                </article>
                <FooterSocial
                  url={window.location.href}
                  title={partenaire.title || ''}
                  titleclass="mt-md-3 mt-lg-5 mb-3"
                  clickTagManager={socialName => this.clickTagManager(socialName)}
                />
              </div>
            </div>
          </section>
        </div>
        <Sidebar sidebarContent={sidebarContent} />
      </>
    );
  };

  renderSidebar = () => {
    const { sidebar, pageDetailSidebar, template, pageDetailTemplate } = this.props;

    if (pageDetailTemplate === 'page-hub_recrutement' || pageDetailTemplate === 'page-emplois_liste_offre') {
      return <JobSidebar />;
    }

    switch (template) {
      case 'page-hub_recrutement':
      case 'page-emplois_liste_offre':
        return <JobSidebar />;

      default: {
        let sidebarContent;
        if (pageDetailSidebar.length > 0) {
          sidebarContent = pageDetailSidebar;
        } else {
          sidebarContent = sidebar;
        }

        return <Sidebar sidebarContent={sidebarContent} />;
      }
    }
  };

  /**
   * nb de rendu avant modif = 5
   * le composant doit être rendu :
   * - au montage lorsque le fichier Routes l'importe car on a appelé la route
   * - les données sont fetch à ce moment-là donc on a un renu à chaque modification du status -> LOADING et SUCCESS (2 rendus) -> comme on fait rien du LOADING -> ne pas update le composent si LOADING ? -> après modif => 4 rendus
   */
  render() {
    const {
      location: { pathname },
      partenaire,
      template
    } = this.props;

    if (pathname.includes('--album')) {
      return <PageMediaDetailContainer />;
    }

    if (this.props.albumsStatus === STATUS_LOADING) {
      return <Loading isWhite />;
    }

    if (this.props.status === STATUS_UNKNOWN) {
      return <Redirect to="/404" />;
    }

    if (partenaire && partenaire.id > 0) {
      return (
        <div className="container">
          <div className="row">{this._renderContentPartenaire()}</div>
        </div>
      );
    }

    if (template === 'mediatheque') {
      return <PageMedia {...this.props} />;
    }

    return (
      <>
        {this.renderPageNodeHeader()}

        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9" ref={r => (this._articleContent = r)}>
                    {this.renderSubRouter()}
                  </div>
                </div>
              </section>
            </div>

            {this.renderSidebar()}
          </div>
        </div>
      </>
    );
  }
}

export default PageNode;
